import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { HttpApiResponse, HttpApiResponseState } from '@core/http';
import { TINY_TASK_TIME } from '@core/performance';
import { yieldToMain } from '@core/performance/utils';
import { RAPID_VIEWS_ID, RAPID_VIEWS_NAME_ID } from '@core/referentials/constants';
import { entityType } from '@entities/helpers';
import { Entity } from '@entities/models';
import { BaseMixinService } from '@mixins/services';
import { CoreServiceMixin } from '@mixins/services/core';
import { isNullishArray } from '@utils/array';
import { clearTimeouts } from '@utils/clear-timeouts';
import { µsetTimeout } from '@utils/function/timer';
import { isNullishNumber } from '@utils/number';
import { isNullish } from '@utils/object';
import { WizardBasketEvent } from '@wizard/events';
import { WizardResultEntity } from '@wizard/models';
import { WizardBasketController } from '@wizard/services';
import { BehaviorSubject, Observable, Subject, distinctUntilChanged, shareReplay } from 'rxjs';
import { BreadcrumbGroups, GhostRootLineageDataEntity } from '../constants';
import { WizardResultUpdateEvent } from '../events';
import { LineageHttpBody, WizardResultsLineageDataEntity, WizardResultsLineageEntity, WizardResultsLineageEntityList } from '../models';
import { WizardResult, WizardResultList, WizardResultMode, WizardResultModes, WizardResultsGroupType } from '../types';
import { WizardResultsApiService } from './wizard-results-api.service';
import { WizardResultsUIService } from './wizard-results-ui.service';

@Injectable({ providedIn: 'root' })
export class WizardResultsController extends CoreServiceMixin(BaseMixinService) {

  /** @Inject */
  readonly #wizardBasketController = inject(WizardBasketController);
  readonly #apiService = inject(WizardResultsApiService);
  readonly #uiService = inject(WizardResultsUIService);

  readonly #groupResultsData = new Map<WizardResultsGroupType, BehaviorSubject<WizardResultList>>();
  readonly #groupResultsDataObs = new Map<WizardResultsGroupType, Observable<WizardResultList>>();
  readonly #selectedGroupItem = new Map<WizardResultsGroupType, BehaviorSubject<WizardResult>>();
  readonly #groupFetching = new Map<WizardResultsGroupType, boolean>();
  readonly #fetchingTimerIds = new Map<WizardResultsGroupType, number>();

  readonly specification$ = new BehaviorSubject<string>(null);
  readonly currentGroup$ = new BehaviorSubject<WizardResultsGroupType>(null);
  readonly currentSelectedGroupItem$ = new BehaviorSubject<WizardResult>(null);
  readonly lineageData$ = new BehaviorSubject<WizardResultsLineageEntity<WizardResultsLineageDataEntity>>(null);
  readonly update$ = new Subject<WizardResultUpdateEvent>();

  #basketMode: WizardResultMode = WizardResultModes.Default;
  #firstGroup: WizardResultsGroupType = 'Reports';

  constructor() {
    super();
    BreadcrumbGroups.forEach(group => {
      this.#groupResultsData.set(group, new BehaviorSubject(null));
      this.#groupResultsDataObs.set(group, this.#groupResultsData.get(group).asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false })));
      this.#selectedGroupItem.set(group, new BehaviorSubject(null));
      this.#groupFetching.set(group, false);
      this.#fetchingTimerIds.set(group, undefined);
    });

    this.#uiService.currentGroup$
      .pipe(distinctUntilChanged())
      .subscribe(this.onGroupChanged.bind(this));

    this.#wizardBasketController.basketChanges()
      .subscribe(this.onBasketChanges.bind(this));
  }

  get basketMode() { return this.#basketMode; }
  get firstGroup() { return this.#firstGroup; }

  get basket() { return this.#wizardBasketController.basket; }
  get currentGroup() { return this.currentGroup$.getValue(); }
  get currentSelectedGroupItem() { return this.#selectedGroupItem.get(this.currentGroup); }
  get currentSelectedGroupItemValue() { return this.#selectedGroupItem.get(this.currentGroup).getValue(); }
  get mainObject() { return this.#selectedGroupItem.get(this.currentGroup).value?.mainObject; }

  observeGroupResultsData(group: WizardResultsGroupType): Observable<WizardResultList> {
    return this.#groupResultsDataObs.get(group);
  }

  observeSelectedGroupItem(group: WizardResultsGroupType): Observable<WizardResult> {
    return this.#selectedGroupItem.get(group).asObservable();
  }

  select(item: WizardResult) {
    if (item) {
      this.currentSelectedGroupItem.next(item);
      this.currentSelectedGroupItem$.next(item);
    } else {
      this.currentSelectedGroupItem.next(null);
      this.currentSelectedGroupItem$.next(null);
    }
  }

  getGroupResultsData(group: WizardResultsGroupType): WizardResultList {
    return this.#groupResultsData.get(group).getValue();
  }

  fetchDataForGroup(group: WizardResultsGroupType, abort = true) {
    if (!this.injected.locationService.pathName.includes(this.getAppConfig('wizardResultsRoute'))) { return; }
    if (!this.basket || !this.basket.data) { return; }

    if (this.#groupFetching.get(group)) {
      this.#groupFetching.set(group, false);
      this.#uiService.setGroupFetching(group, false);

      if (group === 'Reports') {
        if (this.#groupFetching.get('Semantics')) {
          this.#groupFetching.set('Semantics', false);
          this.#uiService.setGroupFetching('Semantics', false);
        }
        if (this.#groupFetching.get('Views')) {
          this.#groupFetching.set('Views', false);
          this.#uiService.setGroupFetching('Views', false);
        }
      }
    }

    if (abort) { this.#apiService.abortAllFetching(); }

    this.#groupFetching.set(group, true);
    this.#uiService.setGroupFetching(group, true);
    this.#groupResultsData.get(group).next(null);
    this.#selectedGroupItem.get(group).next(null);

    clearTimeouts([this.#fetchingTimerIds.get(group)]);

    this.#fetchingTimerIds.set(group, undefined);

    const timerId = µsetTimeout(
      () => this.#apiService.getBasketData({
        group,
        basket: this.basket.data,
        referential: this.injected.referentialsController.getReferential(this.basket.referentialId).name,
        versionId: this.basket.versionId,
        bwMigration: Boolean(this.basket.bwMigration)
      }).subscribe(response => this.onDataFetched(response, group))
      , TINY_TASK_TIME
    );
    this.#fetchingTimerIds.set(group, timerId);
  }

  fetchLineage() {
    if (!this.mainObject) { return; }

    const wizardObjects: WizardResultList = [this.mainObject as unknown as WizardResult];
    const combinedObjects = this.#selectedGroupItem.get(this.currentGroup).value['combinedObjects']?.map(co => co) as WizardResultList;

    if (!isNullishArray(combinedObjects)) { combinedObjects.forEach(combined => wizardObjects.push(combined)); }

    this.lineageData$.next(null);

    this.#apiService.fetchLineage<LineageHttpBody, HttpApiResponse<WizardResultsLineageEntityList<WizardResultsLineageDataEntity>>>(
      this.currentGroup,
      this.mainObject.id,
      {
        wizardObjects,
        basket: this.basket.data
      } as LineageHttpBody,
      this.injected.referentialsController.getReferentialById(this.mainObject.referentialId).name,
      this.mainObject.versionId
    ).subscribe(this.onLineageFetched.bind(this));
  }

  clear() {
    BreadcrumbGroups.forEach(group => {
      this.#groupResultsData.get(group).complete();
      this.#selectedGroupItem.get(group).complete();
    });
    this.currentSelectedGroupItem$.complete();
    this.#groupResultsData.clear();
    this.#uiService.resetUI();
  }

  async reset(): Promise<void> {
    return await new Promise(resolve => {
      this.#apiService.abortAllFetching();

      BreadcrumbGroups.forEach(group => {
        this.#selectedGroupItem.get(group).next(null);
        this.#groupResultsData.get(group).next(null);
        this.#uiService.setGroupFetching(group, true);
        if (this.#groupFetching.get(group)) { this.#groupFetching.set(group, false); };
      });

      this.#uiService.fetching = true;

      this.currentSelectedGroupItem$.next(null);
      this.#uiService.resetUI().resetAllGroupStatus().selectGroup(BreadcrumbGroups[0]);
      this.fireUpdate({ basketUpdated: true });

      return resolve();
    });
  }

  openEntity(entity: Entity) {
    return (
      this.injected.navigationService.navigateToNewTab(
        entityType(entity),
        entity.id,
        this.queryParamsForEntityNavigation(entity)
      )
    );
  }

  async copyRecipeToSpecification(recipe: string): Promise<boolean> {
    return await this.#wizardBasketController.copyRecipeToSpecification(recipe).then((_basket) => {
      this.#uiService.setCurrentTab(1);
      return true;
    });
  }

  async refreshBasket() {
    await this.reset().then(() => {
      this.#uiService.resetUI();
      this.currentGroup$.next('Reports');
      this.onGroupChanged('Reports', true);
    });
  }

  private async onBasketChanges(event: WizardBasketEvent) {
    if (!event?.basket) { return; }

    if (event.action === 'New') {
      await this.reset().then(() => this.#resetAndSetGroup());
    } else {
      this.#checkUIFromBasketState();
      yieldToMain();
    }
  }

  #resetAndSetGroup() {
    this.#uiService.resetUI();
    this.#checkUIFromBasketState();
    this.currentGroup$.next(this.#firstGroup);
    this.onGroupChanged(this.#firstGroup, true);
  }

  #checkUIFromBasketState() {
    if (this.basket) {
      const isBW = Boolean(this.basket.bwMigration);

      this.#basketMode = isBW
        ? WizardResultModes.BW
        : WizardResultModes.Default;

      this.#firstGroup = isBW
        ? 'Views'
        : 'Reports';

      this.#uiService.mode = this.#basketMode;
    }
  }

  private onGroupChanged(group: WizardResultsGroupType, force = false) {
    const results = this.#groupResultsData.get(group).getValue();

    if (this.currentGroup !== group) { this.currentGroup$.next(group); }

    if (force) {
      this.fetchDataForGroup(group);
    } else if (!results) {
      this.fetchDataForGroup(group);
    } else {
      this.select((results.length >= 1) ? results[0] : {} as WizardResultEntity);
      this.fireUpdate({ group, results });
    }
  }

  private onDataFetched(response: HttpApiResponse<WizardResultList>, group: WizardResultsGroupType) {
    if (!response) { return; }

    if (response.state === HttpApiResponseState.Success) {
      const results = !isNullish(response.result)
        ? response.result
        : [];

      this.#groupResultsData.get(group).next(results);

      if (this.currentGroup === group && results.length >= 1) { this.select(results[0]); }

      if (group !== 'Reports' && !this.#uiService.isGroupFetching('Reports') && isNullishArray(this.#groupResultsData.get('Reports').getValue())) {
        const groupWithData = BreadcrumbGroups.find(bcGroup => !isNullishArray(this.#groupResultsData.get(bcGroup).getValue()));
        if (groupWithData) {
          this.#uiService.selectGroup(groupWithData);
        }
      }

      this.fireUpdate({ group, results });

    } else {
      // TODO manage error
    }

    this.#groupFetching.set(group, false);
    this.#uiService.setGroupFetching(group, false);
    this.#uiService.fetching = false;

    if (group === 'Reports') {
      ['Semantics', 'Views'].forEach((group: WizardResultsGroupType) => this.fetchDataForGroup(group, false));
    }
  }

  private onLineageFetched(response: HttpApiResponse<WizardResultsLineageEntityList<WizardResultsLineageDataEntity>>) {
    if (response.state === HttpApiResponseState.Success) {
      this.prepareLineage(response.result);
    } else {
      // TODO do something for error
    }
  }

  private prepareLineage(lineageData: WizardResultsLineageEntityList<WizardResultsLineageDataEntity>) {
    if (!isNullishArray(lineageData)) {
      if (lineageData.length === 1) {
        this.lineageData$.next(lineageData[0]);
      } else {
        const dataLineage = GhostRootLineageDataEntity();

        dataLineage.children = lineageData.map((data) => data.dataLineage).filter(child => !isNullishNumber(child.id));

        const lineage = {
          depth: 1,
          dataLineage
        } as WizardResultsLineageEntity<WizardResultsLineageDataEntity>;

        this.lineageData$.next(lineage);
      }
    } else {
      this.lineageData$.next({} as WizardResultsLineageEntity<WizardResultsLineageDataEntity>);
    }
  }

  private queryParamsForEntityNavigation(entity: Entity): Params {
    const referentialId = entity.referentialId ?? this.mainObject.referentialId;
    const params = {
      referential: referentialId === RAPID_VIEWS_ID
        ? RAPID_VIEWS_NAME_ID
        : this.injected.referentialsController.getReferentialById(referentialId).name,
      tab: 0
    };

    if (params.referential === RAPID_VIEWS_NAME_ID) { params['version'] = entity.versionId ?? this.mainObject.versionId; }

    return params;
  }

  private fireUpdate(event?: WizardResultUpdateEvent) {
    this.update$.next({
      ...event,
      timeStamp: performance.now()
    });
  }
}
